import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { Observable, Subject, Subscription } from 'rxjs';
import { take, tap } from 'rxjs/operators';

import {
  LayoutUtilsService,
  MessageType,
} from '../../../../../../core/_base/crud';
import { AppState } from '../../../../../../core/reducers';
import {
  GCActionFail,
  GCActionSuccess,
  GConfig,
  GConfigActionTypes,
  GConfigBulkUpdate,
  selectGConfigById,
} from '../../../../../../core/ronex';

@Component({
  selector: 'kt-acs1',
  templateUrl: './acs1.component.html',
  styleUrls: ['./acs1.component.scss'],
})
export class Acs1Component implements OnInit, OnDestroy {
  @Input() saveEvent: Observable<void>;

  acs1_api_endpoint: GConfig = new GConfig();
  acs1_api_key: GConfig = new GConfig();
  acs1_company_id: GConfig = new GConfig();
  acs1_company_password: GConfig = new GConfig();
  acs1_user_id: GConfig = new GConfig();
  acs1_user_password: GConfig = new GConfig();
  acs1_billing_code: GConfig = new GConfig();
  acs1_recipient_country: GConfig = new GConfig();
  acs1_cyprus_economy: GConfig = new GConfig();

  hasFormErrors = false;
  serverError: Subject<any>;
  csForm: UntypedFormGroup;
  private subscriptions: Subscription[] = [];

  constructor(
    private store: Store<AppState>,
    private layoutUtilsService: LayoutUtilsService,
    private actions$: Actions,
    private csFB: UntypedFormBuilder
  ) {}

  ngOnInit() {
    this.serverError = new Subject();
    this.serverError.next(false);

    const ss = this.saveEvent.subscribe(() => this.save());
    this.subscriptions.push(ss);

    this.fetchDataFromStore();
    this.createForm();

    const sss = this.actions$
      .pipe(
        ofType<GCActionSuccess>(GConfigActionTypes.GCActionSuccess),
        tap(({ payload }) => {
          this.layoutUtilsService.showActionNotification(
            payload.result,
            MessageType.Update,
            5000,
            true,
            true
          );
        })
      )
      .subscribe();
    this.subscriptions.push(sss);

    const sf = this.actions$
      .pipe(
        ofType<GCActionFail>(GConfigActionTypes.GCActionFail),
        tap(({ payload }) => {
          if (
            // eslint-disable-next-line no-prototype-builtins
            payload.result.hasOwnProperty('error') &&
            // eslint-disable-next-line no-prototype-builtins
            payload.result.error.hasOwnProperty('detail')
          ) {
            this.serverError.next(payload.result.error.detail);
          } else {
            this.serverError.next(
              'Συνέβη ένα σφάλμα με την αποθήκευση των δεδομένων. Παρακαλώ ξαναπροσπαθήστε.'
            );
          }
        })
      )
      .subscribe();
    this.subscriptions.push(sf);
  }

  fetchDataFromStore() {
    this.store
      .pipe(
        select(selectGConfigById('hidden__ACS1_API_ENDPOINT')),
        take(1),
        tap(res => (this.acs1_api_endpoint = { ...res }))
      )
      .subscribe();

    this.store
      .pipe(
        select(selectGConfigById('hidden__ACS1_API_KEY')),
        take(1),
        tap(res => (this.acs1_api_key = { ...res }))
      )
      .subscribe();

    this.store
      .pipe(
        select(selectGConfigById('hidden__ACS1_COMPANY_ID')),
        take(1),
        tap(res => (this.acs1_company_id = { ...res }))
      )
      .subscribe();

    this.store
      .pipe(
        select(selectGConfigById('hidden__ACS1_COMPANY_PASSWORD')),
        take(1),
        tap(res => (this.acs1_company_password = { ...res }))
      )
      .subscribe();

    this.store
      .pipe(
        select(selectGConfigById('hidden__ACS1_USER_ID')),
        take(1),
        tap(res => (this.acs1_user_id = { ...res }))
      )
      .subscribe();

    this.store
      .pipe(
        select(selectGConfigById('hidden__ACS1_USER_PASSWORD')),
        take(1),
        tap(res => (this.acs1_user_password = { ...res }))
      )
      .subscribe();

    this.store
      .pipe(
        select(selectGConfigById('hidden__ACS1_BILLING_CODE')),
        take(1),
        tap(res => (this.acs1_billing_code = { ...res }))
      )
      .subscribe();

    this.store
      .pipe(
        select(selectGConfigById('hidden__ACS1_RECIPIENT_COUNTRY')),
        take(1),
        tap(res => (this.acs1_recipient_country = { ...res }))
      )
      .subscribe();

    this.store
      .pipe(
        select(selectGConfigById('hidden__ACS1_CYPRUS_ECONOMY')),
        take(1),
        tap(res => (this.acs1_cyprus_economy = { ...res }))
      )
      .subscribe();
  }

  createForm() {
    const fgd: { [k: string]: any } = {
      acs1_api_endpoint: [this.acs1_api_endpoint.value, Validators.required],
      acs1_api_key: [this.acs1_api_key.value, Validators.required],
      acs1_company_id: [this.acs1_company_id.value, Validators.required],
      acs1_company_password: [
        this.acs1_company_password.value,
        Validators.required,
      ],
      acs1_user_id: [this.acs1_user_id.value, Validators.required],
      acs1_user_password: [this.acs1_user_password.value, Validators.required],
      acs1_billing_code: [this.acs1_billing_code.value, Validators.required],
      acs1_recipient_country: [
        this.acs1_recipient_country.value,
        Validators.required,
      ],
      acs1_cyprus_economy: [this.acs1_cyprus_economy.value],
    };

    this.csForm = this.csFB.group(fgd);
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sb => sb.unsubscribe());
  }

  save() {
    this.hasFormErrors = false;
    this.serverError.next(false);

    const controls = this.csForm.controls;
    if (this.csForm.invalid) {
      Object.keys(controls).forEach(controlName =>
        controls[controlName].markAsTouched()
      );

      this.hasFormErrors = true;
      return;
    }

    this.acs1_api_endpoint.value = controls.acs1_api_endpoint.value;
    this.acs1_api_key.value = controls.acs1_api_key.value;
    this.acs1_company_id.value = controls.acs1_company_id.value;
    this.acs1_company_password.value = controls.acs1_company_password.value;
    this.acs1_user_id.value = controls.acs1_user_id.value;
    this.acs1_user_password.value = controls.acs1_user_password.value;
    this.acs1_billing_code.value = controls.acs1_billing_code.value;
    this.acs1_recipient_country.value = controls.acs1_recipient_country.value;
    this.acs1_cyprus_economy.value = controls.acs1_cyprus_economy.value;

    const buo: { [key: string]: any } = {};
    buo[this.acs1_api_endpoint.identifier] = this.acs1_api_endpoint.value;
    buo[this.acs1_api_key.identifier] = this.acs1_api_key.value;
    buo[this.acs1_company_id.identifier] = this.acs1_company_id.value;
    buo[this.acs1_company_password.identifier] =
      this.acs1_company_password.value;
    buo[this.acs1_user_id.identifier] = this.acs1_user_id.value;
    buo[this.acs1_user_password.identifier] = this.acs1_user_password.value;
    buo[this.acs1_billing_code.identifier] = this.acs1_billing_code.value;
    buo[this.acs1_recipient_country.identifier] =
      this.acs1_recipient_country.value;
    buo[this.acs1_cyprus_economy.identifier] = this.acs1_cyprus_economy.value;

    this.store.dispatch(new GConfigBulkUpdate({ values: buo }));
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onAlertClose($event) {
    this.hasFormErrors = false;
    this.serverError.next(false);
  }
}
